import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { hrms } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { theme } from '../../theme';
import { SearchableSelect } from '../../components/common/SearchableSelect';

const TODAY = new Date().toISOString().slice(0, 10);

const inp = {
  width: '100%', padding: '0.625rem 0.875rem',
  border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem',
  fontSize: '0.875rem', color: theme.text, background: theme.white,
  outline: 'none', boxSizing: 'border-box',
};

const inpReadOnly = { ...inp, background: '#f9fafb', color: theme.textMuted, cursor: 'not-allowed' };

function Field({ label, required, children, fullWidth }) {
  return (
    <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: theme.text, marginBottom: '0.4rem' }}>
        {label} {required && <span style={{ color: theme.danger }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <Card title={title} style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' }}>
        {children}
      </div>
    </Card>
  );
}

function EmployeeForm() {
  const navigate = useNavigate();
  const { id }   = useParams();
  const isEdit   = !!id;

  const [form, setForm] = useState({
    // Name
    aadhaar_name:         '',
    name_as_per_aadhar:   '',
    // Employment
    employment_type:      'full_time',
    attendance_type:      'general',
    employee_code:        '',
    work_email:           '',
    date_of_joining:      TODAY,
    guest_house_required: '',
    department_id:        '',
    team_id:              '',
    position_id:          '',
    reporting_manager_id: '',
    grade_id:             '',
    contractor_id:        '',
    employment_status:    'active',
    // Personal
    dob:               '',
    gender:            '',
    blood_group:       '',
    marital_status:    '',
    qualification:     '',
    father_husband_name: '',
    phone:             '',
    alternate_phone:   '',
    personal_email:    '',
    emergency_contact: '',
    address:           '',
    present_address:   '',
    // Identity
    aadhar_number:     '',
    pan_number:        '',
    // PF & ESI
    pf_uan:            '',
    esic_number:       '',
    esi_hospital:      '',
  });
  const [grades, setGrades] = useState([]);
  const [contractors, setContractors] = useState([]);

  const [departments,   setDepartments]   = useState([]);
  const [allTeams,      setAllTeams]      = useState([]);
  const [allPositions,  setAllPositions]  = useState([]);
  const [employees,     setEmployees]     = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');

  // ── Additional sections (edit-only) ──────────────────────────────────────
  const [bankAccounts,  setBankAccounts]  = useState([]);
  const [nominees,      setNominees]      = useState([]);
  const [empDocs,       setEmpDocs]       = useState([]);
  const [history,       setHistory]       = useState({ education: [], skills: [], certifications: [], experience: [] });

  // Modals
  const [modal, setModal] = useState(null); // 'bank'|'nominee'|'edu'|'skill'|'cert'|'exp'
  const [editingId, setEditingId] = useState(null);
  const [subSaving, setSubSaving] = useState(false);
  const [bankForm, setBankForm] = useState({ bank_name: '', bank_branch: '', ifsc_code: '', account_number: '', account_type: 'savings', account_holder: '', is_primary: false });
  const [nomineeForm, setNomineeForm] = useState({ name: '', relation: '', dob: '', aadhar: '', contact: '', percentage: '' });
  const [eduForm, setEduForm] = useState({ degree: '', institution: '', field_of_study: '', start_year: '', end_year: '' });
  const [skillForm, setSkillForm] = useState({ skill_name: '', proficiency_level: 'intermediate' });
  const [certForm, setCertForm] = useState({ name: '', issuing_org: '', issue_date: '', expiry_date: '' });
  const [expForm, setExpForm] = useState({ company_name: '', designation: '', from_date: '', to_date: '', description: '' });
  const [docUploading, setDocUploading] = useState(false);
  const fileInputRef = useRef(null);
  const certFileRef = useRef(null);

  // Track whether initial data has loaded (so department change doesn't wipe pre-filled team/position)
  const initializedRef = useRef(false);

  // Load lookup data and employee (edit) on mount
  useEffect(() => {
    Promise.all([
      hrms.getDepartments(),
      hrms.getTeams(),
      hrms.getPositions(),
      hrms.getEmployees({ limit: 500 }),
      hrms.getGrades(),
      hrms.getContractors(),
    ]).then(([deptRes, teamRes, posRes, empRes, gradeRes, contrRes]) => {
      setDepartments(deptRes.data || []);
      setAllTeams(teamRes.data || []);
      setAllPositions(posRes.data || []);
      const d = empRes.data;
      setEmployees(Array.isArray(d) ? d : d.items || []);
      setGrades(gradeRes.data || []);
      setContractors(contrRes.data || []);
    }).catch(() => {});

    if (isEdit) {
      loadEmployee();
      loadSubData();
    }
  }, [isEdit, id]); // eslint-disable-line

  const loadSubData = () => {
    if (!id) return;
    Promise.all([
      hrms.getBankAccounts(id),
      hrms.getNominees(id),
      hrms.getEmpDocuments(id),
      hrms.getEmployeeHistory(id),
    ]).then(([ba, nom, docs, hist]) => {
      setBankAccounts(ba.data || []);
      setNominees(nom.data || []);
      setEmpDocs(docs.data || []);
      const h = hist.data || {};
      setHistory({
        education:      h.education      || [],
        skills:         h.skills         || [],
        certifications: h.certifications || [],
        experience:     h.experience     || [],
      });
    }).catch(() => {});
  };

  // Auto-generate employee code when employment_type changes (new only)
  useEffect(() => {
    if (isEdit) return;
    hrms.getNextEmpCode(form.employment_type)
      .then(r => setForm(p => ({ ...p, employee_code: r.data.code })))
      .catch(() => {});
  }, [form.employment_type, isEdit]);

  const loadEmployee = async () => {
    try {
      const r = await hrms.getEmployee(id);
      const e = r.data;
      initializedRef.current = false;
      setForm({
        // Name
        aadhaar_name:         e.full_name || '',
        name_as_per_aadhar:   e.name_as_per_aadhar || '',
        // Employment
        employment_type:      e.employment_type || 'full_time',
        attendance_type:      e.attendance_type || 'general',
        employee_code:        e.emp_code || '',
        work_email:           e.email || '',
        date_of_joining:      e.hire_date ? e.hire_date.split('T')[0] : TODAY,
        guest_house_required: e.guest_house_required === true ? 'yes' : e.guest_house_required === false ? 'no' : '',
        department_id:        e.department_id        ?? '',
        team_id:              e.team_id              ?? '',
        position_id:          e.position_id          ?? '',
        reporting_manager_id: e.reporting_manager_id ?? '',
        grade_id:             e.grade?.id            ?? '',
        contractor_id:        e.contractor?.id       ?? '',
        employment_status:    e.employment_status     || 'active',
        // Personal
        dob:               e.dob ? e.dob.split('T')[0] : '',
        gender:            e.gender            || '',
        blood_group:       e.blood_group       || '',
        marital_status:    e.marital_status    || '',
        qualification:     e.qualification     ? e.qualification.toUpperCase()         : '',
        father_husband_name: e.father_husband_name ? e.father_husband_name.toUpperCase() : '',
        phone:             e.phone             || '',
        alternate_phone:   e.alternate_phone   || '',
        personal_email:    e.personal_email    || '',
        emergency_contact: e.emergency_contact || '',
        address:           e.address           ? e.address.toUpperCase()               : '',
        present_address:   e.present_address   ? e.present_address.toUpperCase()       : '',
        // Identity
        aadhar_number:     e.aadhar_number     || '',
        pan_number:        e.pan_number        ? e.pan_number.toUpperCase()            : '',
        // PF & ESI
        pf_uan:            e.pf_uan            ? e.pf_uan.toUpperCase()                : '',
        esic_number:       e.esic_number       ? e.esic_number.toUpperCase()           : '',
        esi_hospital:      e.esi_hospital      ? e.esi_hospital.toUpperCase()          : '',
      });
      initializedRef.current = true;
    } catch { setError('Failed to load employee data.'); }
  };

  // Derived filtered lists — filter by department when one is selected
  const filteredTeams = form.department_id
    ? allTeams.filter(t => String(t.department_id) === String(form.department_id))
    : allTeams;

  const filteredPositions = form.department_id
    ? allPositions.filter(p => String(p.department_id) === String(form.department_id))
    : allPositions;

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));
  // Uppercase helper — use for all text inputs except email
  const ups = (key) => (e) => set(key, e.target.value.toUpperCase());

  // When department changes by user interaction, reset team/position
  const handleDepartmentChange = (deptId) => {
    setForm(p => ({ ...p, department_id: deptId, team_id: '', position_id: '' }));
  };

  // Enforce UPPERCASE on all name fields
  const nameChange = (key) => (e) => set(key, e.target.value.toUpperCase());

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.aadhaar_name.trim())   { setError('Name as per Aadhaar is required.'); return; }
    if (!isEdit && !form.work_email) { setError('Work email is required.'); return; }
    if (!form.guest_house_required)  { setError('Guest House Required field is mandatory.'); return; }

    setLoading(true); setError('');
    try {
      if (isEdit) {
        const payload = {
          // Name
          full_name:            form.aadhaar_name,
          name_as_per_aadhar:   form.name_as_per_aadhar || form.aadhaar_name,
          // Employment
          hire_date:            form.date_of_joining     || undefined,
          employment_type:      form.employment_type     || undefined,
          employment_status:    form.employment_status   || undefined,
          attendance_type:      form.attendance_type     || 'general',
          department_id:        form.department_id        ? Number(form.department_id)        : undefined,
          team_id:              form.team_id              ? Number(form.team_id)              : undefined,
          position_id:          form.position_id          ? Number(form.position_id)          : undefined,
          reporting_manager_id: form.reporting_manager_id ? Number(form.reporting_manager_id) : undefined,
          grade_id:             form.grade_id             ? Number(form.grade_id)             : undefined,
          contractor_id:        form.contractor_id        ? Number(form.contractor_id)        : undefined,
          // Personal
          dob:               form.dob               || undefined,
          gender:            form.gender            || undefined,
          blood_group:       form.blood_group       || undefined,
          marital_status:    form.marital_status    || undefined,
          qualification:     form.qualification     || undefined,
          father_husband_name: form.father_husband_name || undefined,
          phone:             form.phone             || undefined,
          alternate_phone:   form.alternate_phone   || undefined,
          personal_email:    form.personal_email    || undefined,
          emergency_contact: form.emergency_contact || undefined,
          address:           form.address           || undefined,
          present_address:   form.present_address   || undefined,
          // Identity
          aadhar_number:     form.aadhar_number     || undefined,
          pan_number:        form.pan_number        || undefined,
          // PF & ESI
          pf_uan:            form.pf_uan            || undefined,
          esic_number:       form.esic_number       || undefined,
          esi_hospital:      form.esi_hospital      || undefined,
        };
        Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
        await hrms.updateEmployee(id, payload);
      } else {
        const fd = new FormData();
        fd.append('full_name',            form.aadhaar_name);
        fd.append('name_as_per_aadhar',   form.name_as_per_aadhar || form.aadhaar_name);
        fd.append('email',                form.work_email);
        fd.append('date_of_joining',      form.date_of_joining);
        fd.append('employment_type',      form.employment_type);
        fd.append('attendance_type',      form.attendance_type || 'general');
        fd.append('guest_house_required', form.guest_house_required === 'yes' ? 'true' : 'false');
        if (form.department_id)        fd.append('department_id',        form.department_id);
        if (form.team_id)              fd.append('team_id',              form.team_id);
        if (form.position_id)          fd.append('position_id',          form.position_id);
        if (form.reporting_manager_id) fd.append('reporting_manager_id', form.reporting_manager_id);
        if (form.contractor_id)        fd.append('contractor_id',        form.contractor_id);
        await hrms.createEmployee(fd);
      }
      navigate('/hrms/employees');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string')   setError(detail);
      else if (Array.isArray(detail))   setError(detail.map(d => d.msg || JSON.stringify(d)).join('; '));
      else setError('Failed to save employee. Please check your inputs.');
    } finally {
      setLoading(false);
    }
  };

  const empTypeLabel = form.employment_type === 'contract' ? 'Contract (BTC)' : 'Full-Time (BT)';

  return (
    <div style={{ maxWidth: '960px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>
            {isEdit ? 'Edit Employee' : 'Add New Employee'}
          </h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
            {isEdit ? 'Update complete employee details' : 'Employee will complete their personal profile after first login'}
          </p>
        </div>
        <Button variant="outlineGray" onClick={() => navigate('/hrms/employees')}>← Back</Button>
      </div>

      {!isEdit && (
        <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '0.5rem', padding: '0.875rem 1rem', marginBottom: '1.5rem', display: 'flex', gap: '0.75rem' }}>
          <span style={{ fontSize: '1.1rem' }}>ℹ️</span>
          <div>
            <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem', color: '#1E40AF' }}>Employee Self-Onboarding</p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#3B82F6' }}>
              Login created with Work Email as username and <strong>Bonton@123</strong> as default password.
              Employee must complete profile, EPF Form 11 and ESI Form 1 after first login.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div style={{ background: '#FEF2F2', border: `1px solid #FECACA`, borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1.5rem', color: theme.danger, fontSize: '0.875rem' }}>
          ⚠️ {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>

        {/* ── Name ── */}
        <Section title="Employee Name">
          <Field label="Name (Display Name)" required fullWidth>
            <input
              style={inp}
              value={form.aadhaar_name}
              onChange={nameChange('aadhaar_name')}
              placeholder="e.g. Rajkumar P"
              required
            />
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.72rem', color: theme.textMuted }}>
              Name used across the system (can be shorter or preferred name)
            </p>
          </Field>
          <Field label="Name as per Aadhaar" fullWidth>
            <input
              style={{ ...inp, textTransform: 'uppercase', letterSpacing: '0.04em' }}
              value={form.name_as_per_aadhar}
              onChange={e => setForm(p => ({ ...p, name_as_per_aadhar: e.target.value.toUpperCase() }))}
              placeholder="AS IT APPEARS ON AADHAAR CARD"
            />
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.72rem', color: theme.textMuted }}>
              Legal name exactly as on Aadhaar card (stored in UPPERCASE)
            </p>
          </Field>
        </Section>

        {/* ── Employment Details ── */}
        <Section title="Employment Details">

          <Field label="Employee Type" required>
            <select
              style={inp}
              value={form.employment_type}
              onChange={e => { set('employment_type', e.target.value); if (e.target.value !== 'contract') set('contractor_id', ''); }}
              required
            >
              <option value="full_time">Full-Time (BT)</option>
              <option value="contract">Contract (BTC)</option>
            </select>
          </Field>

          {form.employment_type === 'contract' && (
            <Field label="Contractor" required>
              <select
                style={inp}
                value={form.contractor_id}
                onChange={e => set('contractor_id', e.target.value)}
                required
              >
                <option value="">— Select Contractor —</option>
                {contractors.map(c => (
                  <option key={c.id} value={c.id}>{c.name}{c.city ? ` (${c.city})` : ''}</option>
                ))}
              </select>
              {contractors.length === 0 && (
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.72rem', color: theme.danger }}>
                  No contractors found. Add one in Departments → Contractor Master.
                </p>
              )}
            </Field>
          )}

          <Field label="Employee ID (Auto-Generated)">
            <input
              style={inpReadOnly}
              value={form.employee_code}
              readOnly
              placeholder="Auto-generated based on type"
            />
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.72rem', color: theme.textMuted }}>
              {form.employment_type === 'contract' ? 'BTC prefix for Contract employees' : 'BT prefix for Full-Time employees'}
            </p>
          </Field>

          <Field label="Work Email" required>
            <input
              style={isEdit ? inpReadOnly : inp}
              type="email"
              value={form.work_email}
              onChange={e => set('work_email', e.target.value)}
              placeholder="name@bonton.com"
              readOnly={isEdit}
              required
            />
          </Field>

          <Field label="Date of Joining" required>
            <input
              style={inp}
              type="date"
              value={form.date_of_joining}
              onChange={e => set('date_of_joining', e.target.value)}
              required
            />
          </Field>

          <Field label="Attendance Type" required>
            <select
              style={inp}
              value={form.attendance_type}
              onChange={e => set('attendance_type', e.target.value)}
            >
              <option value="general">General (Mon–Fri, fixed weekends)</option>
              <option value="flexible_week_off">Flexible Week Off (any one day/week)</option>
            </select>
          </Field>

          <Field label="Guest House Required" required>
            <select
              style={inp}
              value={form.guest_house_required}
              onChange={e => set('guest_house_required', e.target.value)}
              required
            >
              <option value="">Select…</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </Field>

          <Field label="Department">
            <SearchableSelect
              options={departments.map(d => ({ value: d.id, label: d.name }))}
              value={form.department_id}
              onChange={handleDepartmentChange}
              placeholder={departments.length ? 'Search department…' : 'Loading…'}
            />
          </Field>

          <Field label="Team">
            <SearchableSelect
              options={filteredTeams.map(t => ({ value: t.id, label: t.name }))}
              value={form.team_id}
              onChange={v => set('team_id', v)}
              placeholder={filteredTeams.length ? 'Search team…' : (form.department_id ? 'No teams in this department' : 'Search team…')}
            />
          </Field>

          <Field label="Position">
            <SearchableSelect
              options={filteredPositions.map(p => ({ value: p.id, label: p.name }))}
              value={form.position_id}
              onChange={v => set('position_id', v)}
              placeholder={filteredPositions.length ? 'Search position…' : (form.department_id ? 'No positions in this department' : 'Search position…')}
            />
          </Field>

          <Field label="Reporting Manager">
            <SearchableSelect
              options={employees
                .filter(e => !isEdit || e.id !== Number(id))
                .map(e => ({ value: e.id, label: e.full_name, meta: e.emp_code }))}
              value={form.reporting_manager_id}
              onChange={v => set('reporting_manager_id', v)}
              placeholder="Search manager…"
            />
          </Field>

          {isEdit && (
            <Field label="Employment Status">
              <select style={inp} value={form.employment_status} onChange={e => set('employment_status', e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="on_leave">On Leave</option>
              </select>
            </Field>
          )}

          {grades.length > 0 && (
            <Field label="Grade">
              <select style={inp} value={form.grade_id} onChange={e => set('grade_id', e.target.value)}>
                <option value="">— No Grade —</option>
                {grades.map(g => <option key={g.id} value={g.id}>{g.name}{g.years_of_experience ? ` (${g.years_of_experience})` : ''}</option>)}
              </select>
            </Field>
          )}
        </Section>

        {/* ── Personal Information (visible in both create and edit) ── */}
        <Section title="Personal Information">
          <Field label="Date of Birth">
            <input style={inp} type="date" value={form.dob} onChange={e => set('dob', e.target.value)} max={TODAY} />
          </Field>
          <Field label="Gender">
            <select style={inp} value={form.gender} onChange={e => set('gender', e.target.value)}>
              <option value="">— Select —</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Blood Group">
            <select style={inp} value={form.blood_group} onChange={e => set('blood_group', e.target.value)}>
              <option value="">— Select —</option>
              {['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'].map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </Field>
          <Field label="Marital Status">
            <select style={inp} value={form.marital_status} onChange={e => set('marital_status', e.target.value)}>
              <option value="">— Select —</option>
              <option value="single">Single</option>
              <option value="married">Married</option>
              <option value="divorced">Divorced</option>
              <option value="widowed">Widowed</option>
            </select>
          </Field>
          <Field label="Qualification">
            <input style={{ ...inp, textTransform: 'uppercase' }} value={form.qualification} onChange={ups('qualification')} placeholder="e.g. B.TECH, MBA" />
          </Field>
          <Field label="Father / Husband Name">
            <input style={{ ...inp, textTransform: 'uppercase' }} value={form.father_husband_name} onChange={ups('father_husband_name')} placeholder="FATHER OR HUSBAND NAME" />
          </Field>
          <Field label="Phone">
            <input style={inp} type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="10-digit mobile number" />
          </Field>
          <Field label="Alternate Phone">
            <input style={inp} type="tel" value={form.alternate_phone} onChange={e => set('alternate_phone', e.target.value)} placeholder="Alternate number" />
          </Field>
          <Field label="Personal Email">
            <input style={inp} type="email" value={form.personal_email} onChange={e => set('personal_email', e.target.value)} placeholder="personal@email.com" />
          </Field>
          <Field label="Emergency Contact">
            <input style={inp} value={form.emergency_contact} onChange={e => set('emergency_contact', e.target.value)} placeholder="Emergency contact number" />
          </Field>
          <Field label="Permanent Address" fullWidth>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 70, textTransform: 'uppercase' }} value={form.address} onChange={ups('address')} placeholder="PERMANENT ADDRESS..." />
          </Field>
          <Field label="Present Address" fullWidth>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 70, textTransform: 'uppercase' }} value={form.present_address} onChange={ups('present_address')} placeholder="CURRENT RESIDENCE ADDRESS..." />
          </Field>
        </Section>

        {/* ── Identity Documents ── */}
        <Section title="Identity Documents">
          <Field label="Aadhaar Number">
            <input style={inp} value={form.aadhar_number} onChange={e => set('aadhar_number', e.target.value)} placeholder="12-digit Aadhaar number" maxLength={12} />
          </Field>
          <Field label="PAN Number">
            <input style={{ ...inp, textTransform: 'uppercase' }} value={form.pan_number} onChange={e => set('pan_number', e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} />
          </Field>
        </Section>

        {/* ── PF & ESI ── */}
        <Section title="PF & ESI">
          <Field label="PF UAN Number">
            <input style={{ ...inp, textTransform: 'uppercase' }} value={form.pf_uan} onChange={ups('pf_uan')} placeholder="UNIVERSAL ACCOUNT NUMBER" />
          </Field>
          <Field label="ESIC Number">
            <input style={{ ...inp, textTransform: 'uppercase' }} value={form.esic_number} onChange={ups('esic_number')} placeholder="ESI NUMBER" />
          </Field>
          <Field label="ESI Hospital">
            <input style={{ ...inp, textTransform: 'uppercase' }} value={form.esi_hospital} onChange={ups('esi_hospital')} placeholder="NEAREST ESI HOSPITAL" />
          </Field>
        </Section>

        {/* Actions (core details) */}
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', paddingBottom: isEdit ? '1rem' : '2rem' }}>
          <Button type="button" variant="outlineGray" onClick={() => navigate('/hrms/employees')}>Cancel</Button>
          <Button type="submit" variant="primary" loading={loading}>
            {isEdit ? 'Update Employee' : `Add Employee (${empTypeLabel})`}
          </Button>
        </div>
      </form>

      {/* ══════════ EDIT-ONLY: Sub-sections ══════════════════════════════════ */}
      {isEdit && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>

          {/* ── Bank Accounts ── */}
          <Card title="Bank Accounts" titleAction={
            <Button size="sm" variant="primary" onClick={() => { setBankForm({ bank_name: '', bank_branch: '', ifsc_code: '', account_number: '', account_type: 'savings', account_holder: '', is_primary: false }); setEditingId(null); setModal('bank'); }}>+ Add</Button>
          }>
            {bankAccounts.length === 0 ? <p style={{ color: theme.textMuted, fontSize: '0.875rem' }}>No bank accounts added</p> : bankAccounts.map(b => (
              <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '0.6rem 0', borderBottom: `1px solid ${theme.border}` }}>
                <div>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem' }}>{b.bank_name} {b.is_primary && <span style={{ background: '#D1FAE5', color: '#065F46', fontSize: '0.7rem', padding: '0.1rem 0.4rem', borderRadius: '999px', marginLeft: '0.4rem' }}>Primary</span>}</p>
                  <p style={{ margin: '0.15rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>A/C: {b.account_number} · IFSC: {b.ifsc_code || '—'} · {b.account_type}</p>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <Button size="sm" variant="outline" onClick={() => { setBankForm({ ...b }); setEditingId(b.id); setModal('bank'); }}>Edit</Button>
                  <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete?')) { await hrms.deleteBankAccount(id, b.id); loadSubData(); } }}>Del</Button>
                </div>
              </div>
            ))}
          </Card>

          {/* ── Nominees ── */}
          <Card title="Nominees" titleAction={
            <Button size="sm" variant="primary" onClick={() => { setNomineeForm({ name: '', relation: '', dob: '', aadhar: '', contact: '', percentage: '' }); setEditingId(null); setModal('nominee'); }}>+ Add</Button>
          }>
            {nominees.length === 0 ? <p style={{ color: theme.textMuted, fontSize: '0.875rem' }}>No nominees added</p> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead><tr style={{ background: theme.pageBg }}>
                  {['Name', 'Relation', 'Aadhar', '%', ''].map(h => <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', color: theme.textMuted, borderBottom: `1px solid ${theme.border}` }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {nominees.map(n => (
                    <tr key={n.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                      <td style={{ padding: '0.5rem 0.75rem', fontWeight: 500 }}>{n.name}</td>
                      <td style={{ padding: '0.5rem 0.75rem', color: theme.textMuted }}>{n.relation || '—'}</td>
                      <td style={{ padding: '0.5rem 0.75rem', color: theme.textMuted }}>{n.aadhar || '—'}</td>
                      <td style={{ padding: '0.5rem 0.75rem', fontWeight: 700, color: theme.primary }}>{n.percentage}%</td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>
                        <div style={{ display: 'flex', gap: '0.4rem' }}>
                          <Button size="sm" variant="outline" onClick={() => { setNomineeForm({ name: n.name, relation: n.relation || '', dob: n.dob || '', aadhar: n.aadhar || '', contact: n.contact || '', percentage: n.percentage }); setEditingId(n.id); setModal('nominee'); }}>Edit</Button>
                          <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete?')) { await hrms.deleteNominee(id, n.id); loadSubData(); } }}>Del</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {/* ── Documents ── */}
          <Card title="Documents" titleAction={
            <Button size="sm" variant="primary" loading={docUploading} onClick={() => fileInputRef.current?.click()}>+ Upload</Button>
          }>
            <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{ display: 'none' }} onChange={async e => {
              const file = e.target.files?.[0];
              if (!file) return;
              setDocUploading(true);
              try {
                const fd = new FormData();
                fd.append('file', file);
                fd.append('doc_type', file.name.replace(/\.[^.]+$/, ''));
                await hrms.uploadEmpDocument(id, fd);
                loadSubData();
              } catch { alert('Upload failed.'); }
              finally { setDocUploading(false); e.target.value = ''; }
            }} />
            {empDocs.length === 0 ? <p style={{ color: theme.textMuted, fontSize: '0.875rem' }}>No documents uploaded</p> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {empDocs.map(d => (
                  <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: theme.pageBg, borderRadius: '0.375rem' }}>
                    <a href={d.filepath} target="_blank" rel="noreferrer" style={{ color: theme.primary, fontSize: '0.875rem', fontWeight: 500, textDecoration: 'none' }}>📎 {d.doc_type || d.filename || 'Document'}</a>
                    <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete?')) { await hrms.deleteEmpDocument(id, d.id); loadSubData(); } }}>Del</Button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* ── Education ── */}
          <Card title="Education" titleAction={
            <Button size="sm" variant="primary" onClick={() => { setEduForm({ degree: '', institution: '', field_of_study: '', start_year: '', end_year: '' }); setEditingId(null); setModal('edu'); }}>+ Add</Button>
          }>
            {history.education.length === 0 ? <p style={{ color: theme.textMuted, fontSize: '0.875rem' }}>No education records</p> : history.education.map(e => (
              <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.6rem 0', borderBottom: `1px solid ${theme.border}` }}>
                <div>
                  <p style={{ fontWeight: 600, margin: 0 }}>{e.degree}</p>
                  <p style={{ fontSize: '0.8rem', color: theme.textMuted, margin: '0.15rem 0 0' }}>{e.institution}{e.field_of_study ? ` · ${e.field_of_study}` : ''} · {e.start_year}{e.end_year ? `–${e.end_year}` : ''}</p>
                </div>
                <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete?')) { await hrms.deleteEducation(id, e.id); loadSubData(); } }}>Del</Button>
              </div>
            ))}
          </Card>

          {/* ── Skills ── */}
          <Card title="Skills" titleAction={
            <Button size="sm" variant="primary" onClick={() => { setSkillForm({ skill_name: '', proficiency_level: 'intermediate' }); setModal('skill'); }}>+ Add</Button>
          }>
            {history.skills.length === 0 ? <p style={{ color: theme.textMuted, fontSize: '0.875rem' }}>No skills added</p> : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', paddingTop: '0.25rem' }}>
                {history.skills.map(s => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: theme.lightBg, border: `1px solid ${theme.primaryLight}`, borderRadius: '999px', padding: '0.3rem 0.75rem' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: theme.primaryDark }}>{s.skill_name || s.name}</span>
                    <span style={{ fontSize: '0.72rem', color: theme.textMuted }}>· {s.proficiency_level || s.level}</span>
                    <button onClick={async () => { if (window.confirm('Remove?')) { await hrms.deleteSkill(id, s.id); loadSubData(); } }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.danger, fontSize: '0.75rem', padding: 0 }}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* ── Certifications ── */}
          <Card title="Certifications" titleAction={
            <Button size="sm" variant="primary" onClick={() => { setCertForm({ name: '', issuing_org: '', issue_date: '', expiry_date: '' }); setEditingId(null); setModal('cert'); }}>+ Add</Button>
          }>
            {history.certifications.length === 0 ? <p style={{ color: theme.textMuted, fontSize: '0.875rem' }}>No certifications</p> : history.certifications.map(c => (
              <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.6rem 0', borderBottom: `1px solid ${theme.border}` }}>
                <div>
                  <p style={{ fontWeight: 600, margin: 0 }}>{c.name}</p>
                  <p style={{ fontSize: '0.8rem', color: theme.textMuted, margin: '0.15rem 0 0' }}>{c.issuing_org || c.issuer}{c.issue_date ? ` · ${c.issue_date}` : ''}</p>
                </div>
                <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete?')) { await hrms.deleteCertification(id, c.id); loadSubData(); } }}>Del</Button>
              </div>
            ))}
          </Card>

          {/* ── Work Experience ── */}
          <Card title="Work Experience" titleAction={
            <Button size="sm" variant="primary" onClick={() => { setExpForm({ company_name: '', designation: '', from_date: '', to_date: '', description: '' }); setEditingId(null); setModal('exp'); }}>+ Add</Button>
          }>
            {history.experience.length === 0 ? <p style={{ color: theme.textMuted, fontSize: '0.875rem' }}>No experience records</p> : history.experience.map(e => (
              <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.6rem 0', borderBottom: `1px solid ${theme.border}` }}>
                <div>
                  <p style={{ fontWeight: 600, margin: 0 }}>{e.company_name}</p>
                  <p style={{ fontSize: '0.8rem', color: theme.textMuted, margin: '0.15rem 0 0' }}>{e.designation} · {e.from_date}{e.to_date ? ` → ${e.to_date}` : ' → Present'}</p>
                </div>
                <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete?')) { await hrms.deleteExperience(id, e.id); loadSubData(); } }}>Del</Button>
              </div>
            ))}
          </Card>

          {/* ══ Modals ══════════════════════════════════════════════════════ */}
          {modal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', borderRadius: '0.75rem', padding: '1.5rem', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                  <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>
                    {modal === 'bank' && (editingId ? 'Edit Bank Account' : 'Add Bank Account')}
                    {modal === 'nominee' && (editingId ? 'Edit Nominee' : 'Add Nominee')}
                    {modal === 'edu' && 'Add Education'}
                    {modal === 'skill' && 'Add Skill'}
                    {modal === 'cert' && 'Add Certification'}
                    {modal === 'exp' && 'Add Work Experience'}
                  </h2>
                  <button onClick={() => { setModal(null); setEditingId(null); }} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: theme.textMuted }}>&times;</button>
                </div>

                {/* Bank Account form */}
                {modal === 'bank' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {[['bank_name','Bank Name *'], ['bank_branch','Branch'], ['ifsc_code','IFSC Code'], ['account_number','Account Number *'], ['account_holder','Account Holder']].map(([field, label]) => (
                      <div key={field}><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>{label}</label>
                        <input style={{ ...inp, textTransform: 'uppercase' }} value={bankForm[field]} onChange={e => setBankForm(p => ({ ...p, [field]: e.target.value.toUpperCase() }))} /></div>
                    ))}
                    <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Account Type</label>
                      <select style={inp} value={bankForm.account_type} onChange={e => setBankForm(p => ({ ...p, account_type: e.target.value }))}>
                        <option value="savings">Savings</option><option value="current">Current</option>
                      </select>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={bankForm.is_primary} onChange={e => setBankForm(p => ({ ...p, is_primary: e.target.checked }))} />
                      Set as Primary Account
                    </label>
                  </div>
                )}

                {/* Nominee form */}
                {modal === 'nominee' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {[['name','Name *'], ['relation','Relationship'], ['aadhar','Aadhaar No.'], ['contact','Contact']].map(([field, label]) => (
                      <div key={field}><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>{label}</label>
                        <input style={{ ...inp, textTransform: 'uppercase' }} value={nomineeForm[field]} onChange={e => setNomineeForm(p => ({ ...p, [field]: e.target.value.toUpperCase() }))} /></div>
                    ))}
                    <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Date of Birth</label>
                      <input type="date" style={inp} value={nomineeForm.dob} onChange={e => setNomineeForm(p => ({ ...p, dob: e.target.value }))} /></div>
                    <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Percentage %</label>
                      <input type="number" min="0" max="100" style={inp} value={nomineeForm.percentage} onChange={e => setNomineeForm(p => ({ ...p, percentage: e.target.value }))} /></div>
                  </div>
                )}

                {/* Education form */}
                {modal === 'edu' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {[['degree','Degree *','e.g. B.Tech'], ['institution','Institution *','University/College'], ['field_of_study','Field of Study','Computer Science...']].map(([f, l, ph]) => (
                      <div key={f}><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>{l}</label>
                        <input style={{ ...inp, textTransform: 'uppercase' }} value={eduForm[f]} placeholder={ph} onChange={e => setEduForm(p => ({ ...p, [f]: e.target.value.toUpperCase() }))} /></div>
                    ))}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Start Year</label>
                        <input type="number" style={inp} value={eduForm.start_year} placeholder="2018" onChange={e => setEduForm(p => ({ ...p, start_year: e.target.value }))} /></div>
                      <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>End Year</label>
                        <input type="number" style={inp} value={eduForm.end_year} placeholder="2022" onChange={e => setEduForm(p => ({ ...p, end_year: e.target.value }))} /></div>
                    </div>
                  </div>
                )}

                {/* Skill form */}
                {modal === 'skill' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Skill Name *</label>
                      <input style={{ ...inp, textTransform: 'uppercase' }} value={skillForm.skill_name} placeholder="React, Python..." onChange={e => setSkillForm(p => ({ ...p, skill_name: e.target.value.toUpperCase() }))} /></div>
                    <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Proficiency Level</label>
                      <select style={inp} value={skillForm.proficiency_level} onChange={e => setSkillForm(p => ({ ...p, proficiency_level: e.target.value }))}>
                        <option value="beginner">Beginner</option><option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option><option value="expert">Expert</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Certification form */}
                {modal === 'cert' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {[['name','Certification Name *'], ['issuing_org','Issuing Organisation']].map(([f, l]) => (
                      <div key={f}><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>{l}</label>
                        <input style={{ ...inp, textTransform: 'uppercase' }} value={certForm[f]} onChange={e => setCertForm(p => ({ ...p, [f]: e.target.value.toUpperCase() }))} /></div>
                    ))}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      {[['issue_date','Issue Date'], ['expiry_date','Expiry Date']].map(([f, l]) => (
                        <div key={f}><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>{l}</label>
                          <input type="date" style={inp} value={certForm[f]} onChange={e => setCertForm(p => ({ ...p, [f]: e.target.value }))} /></div>
                      ))}
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Certificate File</label>
                      <input ref={certFileRef} type="file" accept=".pdf,.jpg,.png" style={{ fontSize: '0.85rem' }} />
                    </div>
                  </div>
                )}

                {/* Work Experience form */}
                {modal === 'exp' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {[['company_name','Company Name *'], ['designation','Designation *']].map(([f, l]) => (
                      <div key={f}><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>{l}</label>
                        <input style={{ ...inp, textTransform: 'uppercase' }} value={expForm[f]} onChange={e => setExpForm(p => ({ ...p, [f]: e.target.value.toUpperCase() }))} /></div>
                    ))}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>From Date</label>
                        <input type="date" style={inp} value={expForm.from_date} onChange={e => setExpForm(p => ({ ...p, from_date: e.target.value }))} /></div>
                      <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>To Date (blank = Present)</label>
                        <input type="date" style={inp} value={expForm.to_date} onChange={e => setExpForm(p => ({ ...p, to_date: e.target.value }))} /></div>
                    </div>
                    <div><label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Description</label>
                      <textarea style={{ ...inp, resize: 'vertical', minHeight: 70, textTransform: 'uppercase' }} value={expForm.description} onChange={e => setExpForm(p => ({ ...p, description: e.target.value.toUpperCase() }))} /></div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.25rem' }}>
                  <Button variant="outlineGray" onClick={() => { setModal(null); setEditingId(null); }}>Cancel</Button>
                  <Button variant="primary" loading={subSaving} onClick={async () => {
                    setSubSaving(true);
                    try {
                      if (modal === 'bank') {
                        if (editingId) await hrms.updateBankAccount(id, editingId, bankForm);
                        else await hrms.addBankAccount(id, bankForm);
                      } else if (modal === 'nominee') {
                        if (editingId) await hrms.updateNominee(id, editingId, nomineeForm);
                        else await hrms.addNominee(id, nomineeForm);
                      } else if (modal === 'edu') {
                        await hrms.addEducation(id, eduForm);
                      } else if (modal === 'skill') {
                        if (!skillForm.skill_name.trim()) { alert('Skill name required'); setSubSaving(false); return; }
                        await hrms.addSkill(id, skillForm);
                      } else if (modal === 'cert') {
                        const fd = new FormData();
                        fd.append('name', certForm.name);
                        if (certForm.issuing_org) fd.append('issuing_org', certForm.issuing_org);
                        if (certForm.issue_date) fd.append('issue_date', certForm.issue_date);
                        if (certForm.expiry_date) fd.append('expiry_date', certForm.expiry_date);
                        if (certFileRef.current?.files?.[0]) fd.append('file', certFileRef.current.files[0]);
                        await hrms.addCertification(id, fd);
                      } else if (modal === 'exp') {
                        const payload = { ...expForm };
                        if (!payload.to_date) delete payload.to_date;
                        await hrms.addExperience(id, payload);
                      }
                      setModal(null); setEditingId(null); loadSubData();
                    } catch (e) { alert(e?.response?.data?.detail || 'Failed to save.'); }
                    finally { setSubSaving(false); }
                  }}>Save</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default EmployeeForm;
